import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useHomepage } from "src/context/homepage/homepage.provider";
import { useParams } from "react-router-dom";
import { Row, Col, Spinner } from "react-bootstrap";
import { useHubData } from "src/hooks/useHubData";
import { useHubListLivePreview } from "src/hooks/useHubListLivePreview";
import { filterEntertainmentCards } from "src/utils/filterEntertainmentCards";
import { isPayloadSource } from "src/utils/hubSource";
import { fetchEntertainmentEvents } from "src/api/payload/entertainment";
import { mapEntertainmentEvent } from "src/utils/mapEntertainment";
import { entertainmentLivePreviewRequestHandler } from "src/utils/entertainmentLivePreviewHandler";

import Hero from "src/components/Hero/Hero";
import MoreThanShows from "src/components/HomePage/MoreThanShows";
import PgCard from "src/components/Card/PgCard";
import SectionalTabs from "src/components/SectionalTabs/SectionalTabs";
import TitleSection from "src/components/TitleSection/TitleSection";

const buildButtonSettings = (el, t) => {
  if (!el.event_url && !el.ticket_url) return null;

  if (el.event_url && el.ticket_url) {
    return [
      {
        href: el.event_url,
        label: t("button.read-more"),
      },
      {
        href: el.ticket_url,
        label: t("entertainment.buyTickets"),
      },
    ];
  }

  return {
    href: el.event_url || el.ticket_url,
    label: el.event_url ? t("button.read-more") : t("entertainment.buyTickets"),
  };
};

const Entertainment = () => {
  const { t, i18n } = useTranslation();
  const params = useParams();
  const { sectionCategory, sectionDescription } = useHomepage();
  const {
    data: entertainmentDataArr,
    loading,
    error: fetchError,
  } = useHubData(
    "entertainment",
    { fetchPayloadData: fetchEntertainmentEvents, ersKeyPrefix: "entertainment" },
    i18n.language
  );

  const { items: previewEvents, isLivePreview, livePreviewId } =
    useHubListLivePreview(
      entertainmentDataArr ?? [],
      isPayloadSource("entertainment"),
      i18n.language,
      {
        depth: 1,
        mapDoc: mapEntertainmentEvent,
        requestHandler: entertainmentLivePreviewRequestHandler,
        // Prefer Payload document id; fall back to name for ERS-shaped rows.
        getItemId: (item) => item?.id ?? item?.name,
      }
    );

  const error = fetchError ? fetchError : null;
  const visibleCards = filterEntertainmentCards(
    previewEvents,
    params.category,
    { livePreviewId: isLivePreview ? livePreviewId : undefined }
  );

  return (
    <EntertainmentPage>
      <Hero
        bgColor=""
        bgImage={"entertainment/entertainment-hero-image-kiss-bg.jpg"}
        buttonLink=""
        buttonText=""
        descriptionBgColor=""
        image="entertainment/entertainment-logo-2400x1600px.png"
        imageAlt=""
        title=""
      >
        <SectionalTabs
          routeData={{
            route: t("routes.entertainment"),
            routeCategory: "entertainment",
            sectionTitle: t("entertainment.liveEvents"),
            selectCategory: t("entertainment.selectEvent"),
            selectAll: t("entertainment.selectAll"),
          }}
          isDynamic={true}
        />
      </Hero>

      <div>
        <TitleSection
          title={
            sectionCategory && sectionDescription
              ? sectionCategory
              : t("entertainment.forEveryone")
          }
          text={
            sectionDescription
              ? sectionDescription
              : t("entertainment.allEntertainmentText")
          }
        />
      </div>

      <container-wide>
        {loading ? (
          <div className="text-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">{t("loading")}</span>
            </Spinner>
          </div>
        ) : error ? (
          <div className="text-center text-danger">
            <p>
              {t("error.somethingWentWrong")}: {error}
            </p>
          </div>
        ) : (
          <Row className="g-4 gx-3" xs={1} md={2} lg={3}>
            {visibleCards.map((el) => (
              <Col key={el.id ?? el.name}>
                <PgCard
                  id={el.id != null ? String(el.id) : undefined}
                  image={el.image}
                  imageAlt={el.imageAlt || el.name}
                  title={el.name}
                  subtitle={el.event_info}
                  textTitle={el.title}
                  text={el.text}
                  buttonSettings={buildButtonSettings(el, t)}
                />
              </Col>
            ))}
          </Row>
        )}
      </container-wide>
      <EntertainmentFooter>
        <TitleSection
          title={t("entertainment.moreThanShows")}
          as="h2"
          text={t("entertainment.moreThanShowsText")}
        />
        <MoreThanShows />
      </EntertainmentFooter>
    </EntertainmentPage>
  );
};

const EntertainmentPage = styled.div`
  .hero_image_container {
    img {
      padding: 40px;
    }
  }
  .pg-card {
    .card-header {
      flex-grow: 0;
    }
    .card-body {
      display: flex;
      flex-direction: column;
      flex-grow: 1;
      .card-text {
        flex-grow: 1;
      }
    }
  }
`;

const EntertainmentFooter = styled.div`
  background-color: var(--lightblack);
  margin-top: 2rem;
`;

export default Entertainment;
